import {
  Avatar,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  makeStyles,
  Typography
} from "@material-ui/core";
import { Delete as DeleteIcon, Edit as EditIcon, Send as SendIcon } from "@material-ui/icons";
import { useQuery } from "@redux-requests/react";
import { Formik } from "formik";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import * as yup from "yup";
import { BaseLayout, Header, Loader } from "../components";
import { SubmitButton, TextField } from "../components/formik";
import { types } from "../ducks";
import { createForumPost, getForumPosts, getForumTopic, updateForumPost } from "../ducks/actions";
import { EditForumTopicDialog } from "../features/forum";
import { useActionRequest, useInterval, useMediaQuery, useUserMe } from "../hooks";

const ForumTopicPage = () => {
  const isSmallScreen = useMediaQuery("(max-width: 767px)");
  const useStyles = makeStyles(theme => ({
    root: {
      "& > *": {
        marginBottom: theme.spacing(3)
      }
    },
    actions: {
      textAlign: isSmallScreen ? "center" : "right"
    },
    postActions: {
      top: "0%",
      marginTop: "35px"
      // marginTop: "48px",
      // display: "flex",
      // flexDirection: "column"
    },
    postBodyInput: {
      flex: "1 1 100%"
    },
    postBodyCard: {
      boxShadow: "none"
    },
    postBody: {
      whiteSpace: "pre-line"
    },
    postAuthor: {
      textAlign: "right"
    }
  }));

  const intl = useIntl();
  const classes = useStyles();
  const dispatch = useDispatch();
  const { id } = useParams();
  const { user } = useUserMe();

  const [editForumTopic, setEditForumTopic] = useState(false);
  // const [forumPost, setForumPost] = useState(false);
  const [isPolling, setIsPolling] = useState(false);

  useEffect(() => {
    dispatch(getForumTopic({ id }));
  }, [dispatch, id]);
  const {
    data: { data: forumTopic },
    loading: forumTopicLoading
  } = useQuery({ type: types.GET_FORUM_TOPIC, defaultData: { data: {} } });

  useEffect(() => {
    dispatch(getForumPosts({ forumTopicId: id }));
  }, [dispatch, id]);
  const {
    data: { data: forumPosts },
    loading: forumPostsLoading
  } = useQuery({ type: types.GET_FORUM_POSTS, defaultData: { data: [] } });

  const updateForumPostRequest = useActionRequest({
    action: updateForumPost
  });

  const createForumPostRequest = useActionRequest({
    action: createForumPost
  });

  const initialValues = {
    body: ""
  };
  const validationSchema = yup.object().shape({
    body: yup.string().required()
  });
  const onCreatePost = (forumPost, actions) => {
    createForumPostRequest({ ...forumPost, forumTopicId: forumTopic.id });
    actions.setSubmitting(false);
    actions.resetForm();
  };

  useInterval(() => {
    setIsPolling(true);
    dispatch(getForumPosts({ forumTopicId: id }));
  }, 30000);

  return (
    <BaseLayout className={classes.root}>
      <Header icon="bubble-speak" title={forumTopic?.title} helper={forumTopic?.body} />
      <Grid container>
        <Grid item xs={12} sm={6} md={8} lg={9} />
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <div className={classes.actions}>
            {user.profile && user.profile?.id === forumTopic.profile?.id && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => setEditForumTopic(forumTopic.id)}
              >
                <EditIcon />
                &nbsp;
                {intl.formatMessage({
                  id: "Pages.ForumTopicPage.EditTopic",
                  defaultMessage: "Edit Topic"
                })}
              </Button>
            )}
          </div>
        </Grid>
      </Grid>
      <Grid container>
        <Grid item xs={12}>
          <Loader loading={!isPolling && (forumTopicLoading || forumPostsLoading)}>
            <List>
              {forumPosts.map(forumPost => (
                <ListItem alignItems="flex-start">
                  <ListItemAvatar>
                    <Avatar
                      alt={forumPost.profile?.firstName}
                      src={forumPost.profile?.avatarThumb}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Card className={classes.postBodyCard}>
                        <CardContent>
                          <Typography variant="body1" className={classes.postBody}>
                            {forumPost.body}
                          </Typography>
                        </CardContent>
                      </Card>
                    }
                    secondary={
                      <>
                        <br />
                        <Typography variant="body2" className={classes.postAuthor}>
                          <i>
                            {moment(forumPost.createdAt).format("DD/MM/YYYY HH:mm")} by{" "}
                            {`${forumPost.profile?.title} ${forumPost.profile?.firstName} ${forumPost.profile?.lastName}`.trim()}
                          </i>
                        </Typography>
                      </>
                    }
                  />
                  <ListItemSecondaryAction className={classes.postActions}>
                    {user.profile &&
                      (user.profile?.id === forumPost.profile?.id ||
                        user.profile?.id === forumTopic.profile?.id) && (
                        <>
                          <IconButton
                            edge="end"
                            aria-label="delete"
                            size="small"
                            onClick={() =>
                              updateForumPostRequest({ id: forumPost.id, published: false })
                            }
                          >
                            <DeleteIcon />
                          </IconButton>
                        </>
                      )}
                  </ListItemSecondaryAction>
                </ListItem>
              ))}

              <Formik
                validationSchema={validationSchema}
                onSubmit={onCreatePost}
                initialValues={initialValues}
              >
                <ListItem>
                  <ListItemAvatar>
                    <Avatar alt={user?.profile?.firstName} src={user?.profile?.avatarThumb} />
                  </ListItemAvatar>
                  <TextField
                    className={classes.postBodyInput}
                    name="body"
                    variant="outlined"
                    label={intl.formatMessage({
                      id: "Forum.ForumTopicPage.Body",
                      defaultMessage: "Body"
                    })}
                    multiline
                    rows={2}
                  />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" aria-label="send" component={SubmitButton} size="small">
                      <SendIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              </Formik>
            </List>
          </Loader>
        </Grid>
      </Grid>

      {editForumTopic && <EditForumTopicDialog id={id} onClose={() => setEditForumTopic(false)} />}
    </BaseLayout>
  );
};
export default ForumTopicPage;
